/**
 * The Hebrew calendar, enough for a family: convert a civil date to a Hebrew
 * one and back, and find when a Hebrew date next falls. Used for yahrzeits
 * and the chagim in "Coming up". Pure arithmetic (the classic Dershowitz &
 * Reingold formulation), no dependency, no DOM.
 *
 * Months are numbered the traditional way: 1 = Nisan … 7 = Tishrei … 12 = Adar
 * (Adar I in a leap year), 13 = Adar II (leap years only). A Hebrew year begins
 * in Tishrei (month 7).
 */

export interface HebrewDate {
  year: number;
  /** 1 = Nisan … 7 = Tishrei … 12 = Adar / Adar I, 13 = Adar II. */
  month: number;
  day: number;
}

export const NISAN = 1;
export const IYAR = 2;
export const SIVAN = 3;
export const TAMMUZ = 4;
export const AV = 5;
export const ELUL = 6;
export const TISHREI = 7;
export const CHESHVAN = 8;
export const KISLEV = 9;
export const TEVET = 10;
export const SHVAT = 11;
export const ADAR = 12;
export const ADAR_II = 13;

const HEBREW_EPOCH = -1373427; // fixed day number of 1 Tishrei AM 1 (R.D. style, day 1 = 1 Jan 1 CE Gregorian)

/** Days from the civil epoch to a Gregorian date (R.D. fixed day number). */
export function gregorianToFixed(year: number, month: number, day: number): number {
  const y = year - 1;
  let days = 365 * y + Math.floor(y / 4) - Math.floor(y / 100) + Math.floor(y / 400);
  days += Math.floor((367 * month - 362) / 12);
  if (month > 2) days -= isGregorianLeap(year) ? 1 : 2;
  return days + day;
}

export function fixedToGregorian(fixed: number): { year: number; month: number; day: number } {
  const d0 = fixed - 1;
  const n400 = Math.floor(d0 / 146097);
  const d1 = d0 % 146097;
  const n100 = Math.floor(d1 / 36524);
  const d2 = d1 % 36524;
  const n4 = Math.floor(d2 / 1461);
  const d3 = d2 % 1461;
  const n1 = Math.floor(d3 / 365);
  let year = 400 * n400 + 100 * n100 + 4 * n4 + n1;
  if (!(n100 === 4 || n1 === 4)) year += 1;
  const priorDays = fixed - gregorianToFixed(year, 1, 1);
  const correction = fixed < gregorianToFixed(year, 3, 1) ? 0 : isGregorianLeap(year) ? 1 : 2;
  const month = Math.floor((12 * (priorDays + correction) + 373) / 367);
  const day = fixed - gregorianToFixed(year, month, 1) + 1;
  return { year, month, day };
}

function isGregorianLeap(y: number): boolean {
  return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
}

export function isHebrewLeapYear(year: number): boolean {
  return ((7 * year + 1) % 19) < 7;
}

/** 12 or 13. */
export function hebrewMonthsInYear(year: number): number {
  return isHebrewLeapYear(year) ? 13 : 12;
}

/** Days elapsed from the Hebrew epoch to 1 Tishrei of `year` (before dechiyot postponements are settled). */
function hebrewCalendarElapsedDays(year: number): number {
  const monthsElapsed = Math.floor((235 * year - 234) / 19);
  const partsElapsed = 12084 + 13753 * monthsElapsed;
  let day = monthsElapsed * 29 + Math.floor(partsElapsed / 25920);
  if ((3 * (day + 1)) % 7 < 3) day += 1;
  return day;
}

function hebrewYearLengthCorrection(year: number): number {
  const ny0 = hebrewCalendarElapsedDays(year - 1);
  const ny1 = hebrewCalendarElapsedDays(year);
  const ny2 = hebrewCalendarElapsedDays(year + 1);
  if (ny2 - ny1 === 356) return 2;
  if (ny1 - ny0 === 382) return 1;
  return 0;
}

/** Fixed day number of 1 Tishrei (Rosh Hashana) of a Hebrew year. */
export function hebrewNewYear(year: number): number {
  return HEBREW_EPOCH + hebrewCalendarElapsedDays(year) + hebrewYearLengthCorrection(year);
}

export function daysInHebrewYear(year: number): number {
  return hebrewNewYear(year + 1) - hebrewNewYear(year);
}

export function daysInHebrewMonth(year: number, month: number): number {
  if (month === IYAR || month === TAMMUZ || month === ELUL || month === TEVET || month === ADAR_II) return 29;
  if (month === ADAR && !isHebrewLeapYear(year)) return 29;
  if (month === CHESHVAN && daysInHebrewYear(year) % 10 !== 5) return 29;
  if (month === KISLEV && daysInHebrewYear(year) % 10 === 3) return 29;
  return 30;
}

export function hebrewToFixed(h: HebrewDate): number {
  let days = hebrewNewYear(h.year) + h.day - 1;
  if (h.month < TISHREI) {
    // Months after Tishrei up to the end of the year, then Nisan … this month.
    for (let m = TISHREI; m <= hebrewMonthsInYear(h.year); m++) days += daysInHebrewMonth(h.year, m);
    for (let m = NISAN; m < h.month; m++) days += daysInHebrewMonth(h.year, m);
  } else {
    for (let m = TISHREI; m < h.month; m++) days += daysInHebrewMonth(h.year, m);
  }
  return days;
}

export function fixedToHebrew(fixed: number): HebrewDate {
  const approx = Math.floor((fixed - HEBREW_EPOCH) / (35975351 / 98496)) + 1;
  let year = approx - 1;
  while (hebrewNewYear(year + 1) <= fixed) year += 1;
  const start = fixed < hebrewToFixed({ year, month: NISAN, day: 1 }) ? TISHREI : NISAN;
  let month = start;
  while (fixed > hebrewToFixed({ year, month, day: daysInHebrewMonth(year, month) })) month += 1;
  const day = fixed - hebrewToFixed({ year, month, day: 1 }) + 1;
  return { year, month, day };
}

// ---- conveniences on YYYY-MM-DD strings -----------------------------------

export function isoToFixed(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number);
  return gregorianToFixed(y, m, d);
}

export function fixedToISO(fixed: number): string {
  const g = fixedToGregorian(fixed);
  return `${g.year}-${String(g.month).padStart(2, '0')}-${String(g.day).padStart(2, '0')}`;
}

export function hebrewDateOf(iso: string): HebrewDate {
  return fixedToHebrew(isoToFixed(iso));
}

/**
 * The civil date on which the anniversary of a Hebrew date falls in a given
 * Hebrew year, with the customary adjustments: a day 30 that the month lacks
 * that year is kept on the 29th; an Adar date is kept in Adar II in a leap
 * year (the Sephardi custom, which is this family's), and an Adar II date
 * falls in plain Adar in a common year.
 */
export function hebrewAnniversaryInYear(orig: HebrewDate, year: number): number {
  let month = orig.month;
  const leap = isHebrewLeapYear(year);
  if (month === ADAR_II && !leap) month = ADAR;
  if (month === ADAR && leap && !isHebrewLeapYear(orig.year)) month = ADAR_II;
  const day = Math.min(orig.day, daysInHebrewMonth(year, month));
  return hebrewToFixed({ year, month, day });
}

/** Next civil date (YYYY-MM-DD) on or after `fromISO` when the anniversary of the Hebrew date of `origISO` falls. */
export function nextHebrewAnniversary(origISO: string, fromISO: string): { iso: string; hebrew: HebrewDate; years: number } {
  const orig = hebrewDateOf(origISO);
  const from = isoToFixed(fromISO);
  let year = fixedToHebrew(from).year;
  let fixed = hebrewAnniversaryInYear(orig, year);
  if (fixed < from) {
    year += 1;
    fixed = hebrewAnniversaryInYear(orig, year);
  }
  return { iso: fixedToISO(fixed), hebrew: fixedToHebrew(fixed), years: year - orig.year };
}

/** Next civil date on or after `fromISO` of a fixed Hebrew calendar day (a holiday). */
export function nextHebrewDate(month: number, day: number, fromISO: string): { iso: string; year: number } {
  const from = isoToFixed(fromISO);
  let year = fixedToHebrew(from).year;
  const inYear = (y: number) => {
    let m = month;
    if (m === ADAR_II && !isHebrewLeapYear(y)) m = ADAR;
    if (m === ADAR && isHebrewLeapYear(y)) m = ADAR_II; // Purim & co. are kept in Adar II
    return hebrewToFixed({ year: y, month: m, day: Math.min(day, daysInHebrewMonth(y, m)) });
  };
  let fixed = inYear(year);
  if (fixed < from) {
    year += 1;
    fixed = inYear(year);
  }
  return { iso: fixedToISO(fixed), year };
}

const MONTH_NAMES_EN = ['', 'Nisan', 'Iyar', 'Sivan', 'Tammuz', 'Av', 'Elul', 'Tishrei', 'Cheshvan', 'Kislev', 'Tevet', 'Shvat', 'Adar', 'Adar II'];
const MONTH_NAMES_HE = ['', 'ניסן', 'אייר', 'סיון', 'תמוז', 'אב', 'אלול', 'תשרי', 'חשוון', 'כסלו', 'טבת', 'שבט', 'אדר', 'אדר ב׳'];

/** "14 Cheshvan" / "י״ד חשוון" — the month name says Adar I when the year is leap. */
export function formatHebrewDate(h: HebrewDate, lang: 'en' | 'he', withYear = false): string {
  const leap = isHebrewLeapYear(h.year);
  let name = (lang === 'he' ? MONTH_NAMES_HE : MONTH_NAMES_EN)[h.month] ?? '';
  if (h.month === ADAR && leap) name = lang === 'he' ? 'אדר א׳' : 'Adar I';
  if (lang === 'he') return `${hebrewNumeral(h.day)} ${name}${withYear ? ` ${hebrewNumeral(h.year % 1000)}` : ''}`;
  return `${h.day} ${name}${withYear ? ` ${h.year}` : ''}`;
}

/** Hebrew letters for small numbers (days and short years), with the customary 15/16 forms. */
export function hebrewNumeral(n: number): string {
  if (n <= 0) return String(n);
  const ones = ['', 'א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ז', 'ח', 'ט'];
  const tens = ['', 'י', 'כ', 'ל', 'מ', 'נ', 'ס', 'ע', 'פ', 'צ'];
  const hundreds = ['', 'ק', 'ר', 'ש', 'ת'];
  let rest = n;
  let out = '';
  while (rest >= 400) {
    out += 'ת';
    rest -= 400;
  }
  out += hundreds[Math.floor(rest / 100)];
  rest %= 100;
  if (rest === 15) out += 'טו';
  else if (rest === 16) out += 'טז';
  else {
    out += tens[Math.floor(rest / 10)];
    out += ones[rest % 10];
  }
  if (out.length === 1) return `${out}׳`;
  return `${out.slice(0, -1)}״${out.slice(-1)}`;
}
